import type { PDFFont, PDFPage, RGB } from "pdf-lib";
import type { PdfDocModel } from "./model.js";

const OUTFIT_UNSUPPORTED = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{E0020}-\u{E007F}]/gu;

/** §3.9. mode "winansi" additionally applies the Helvetica fallback substitutions. */
export function sanitizeText(raw: string, mode: "unicode" | "winansi"): string {
  let text = raw.replace(/[\r\n\t]+/g, " ").replace(/\s+/g, " ").trim();
  text = text.replace(OUTFIT_UNSUPPORTED, "");

  if (mode === "winansi") {
    text = text
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, "\"")
      .replace(/[\u2013\u2014]/g, "-")
      .replace(/\u2026/g, "...")
      .replace(/[^\x00-\xFF]/g, "");
  }

  return text.replace(/\s+/g, " ").trim();
}

/** Width of text with per-character tracking applied (pdf-lib cannot do this natively). */
export function measureTracked(font: PDFFont, text: string, size: number, tracking: number): number {
  if (text.length === 0) return 0;
  return font.widthOfTextAtSize(text, size) + tracking * (text.length - 1);
}

/** Draws text character-by-character to emulate letter-spacing. tracking 0 → single drawText. */
export function drawTrackedText(
  page: PDFPage,
  text: string,
  opts: { x: number; y: number; font: PDFFont; size: number; color: RGB; tracking: number }
): void {
  if (opts.tracking === 0 || text.length < 2) {
    page.drawText(text, opts);
    return;
  }

  let x = opts.x;
  for (const character of text) {
    page.drawText(character, { ...opts, x });
    x += opts.font.widthOfTextAtSize(character, opts.size) + opts.tracking;
  }
}

function breakWord(word: string, font: PDFFont, size: number, maxWidth: number): string[] {
  if (word.length === 0) return [""];
  const parts: string[] = [];
  let part = "";

  for (const character of word) {
    const candidate = part + character;
    if (part && font.widthOfTextAtSize(candidate, size) > maxWidth) {
      parts.push(part);
      part = character;
    } else {
      part = candidate;
    }
  }

  if (part || parts.length === 0) parts.push(part);
  return parts;
}

function ellipsize(line: string, font: PDFFont, size: number, maxWidth: number): string {
  const ellipsis = "…";
  if (font.widthOfTextAtSize(ellipsis, size) > maxWidth) return "";

  let truncated = line;
  while (truncated && font.widthOfTextAtSize(`${truncated}${ellipsis}`, size) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated.trimEnd()}${ellipsis}`;
}

/**
 * Greedy word wrap to maxWidth. A single word longer than maxWidth is broken by
 * character. Returns at least one line (possibly ""). If maxLines is given, the
 * final line is truncated with "…".
 */
export function wrapText(
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number,
  maxLines?: number
): string[] {
  if (text === "") return [""];
  if (maxWidth <= 0) return [maxLines ? ellipsize(text, font, size, maxWidth) : text];

  const lines: string[] = [];
  let line = "";

  for (const word of text.split(/\s+/)) {
    const wordParts =
      font.widthOfTextAtSize(word, size) > maxWidth ? breakWord(word, font, size, maxWidth) : [word];

    for (const part of wordParts) {
      const candidate = line ? `${line} ${part}` : part;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        line = candidate;
        continue;
      }

      if (line) lines.push(line);
      line = part;

      if (maxLines && lines.length === maxLines) {
        lines[maxLines - 1] = ellipsize(lines[maxLines - 1], font, size, maxWidth);
        return lines;
      }
    }
  }

  lines.push(line);
  if (!maxLines || lines.length <= maxLines) return lines;

  const limited = lines.slice(0, maxLines);
  limited[maxLines - 1] = ellipsize(limited[maxLines - 1], font, size, maxWidth);
  return limited;
}

/** §3.5. */
export function chooseColumnCount(doc: PdfDocModel, includeNotes: boolean): 1 | 2 {
  const items = doc.sections.flatMap((section) => section.groups.flatMap((group) => group.items));
  const maxItemChars = Math.max(0, ...items.map((item) => sanitizeText(item.text, "unicode").length));
  const hasLongNotes =
    includeNotes &&
    items.some((item) => sanitizeText(item.note ?? "", "unicode").length > 60);

  return items.length >= 12 && maxItemChars <= 44 && !hasLongNotes ? 2 : 1;
}

/** Rounded-rect SVG path string for a checkbox of the given side/radius (§3.6). */
export function roundedRectPath(side: number, radius: number): string {
  const r = Math.min(Math.max(radius, 0), side / 2);
  const edge = side - r;
  return [
    `M ${r} 0`,
    `L ${edge} 0`,
    `Q ${side} 0 ${side} ${r}`,
    `L ${side} ${edge}`,
    `Q ${side} ${side} ${edge} ${side}`,
    `L ${r} ${side}`,
    `Q 0 ${side} 0 ${edge}`,
    `L 0 ${r}`,
    `Q 0 0 ${r} 0`,
    "Z",
  ].join(" ");
}

export const CHECK_PATH = "M 0 5.1 L 3.6 8.6 L 10 1.2";
